import { toast } from "sonner";
import { Button } from "./ui/button";
import { CheckCircle, CircleCheck, CopyIcon } from "lucide-react";
import { useState } from "react";

type BaseCopyButtonProps = {
  variant?: "ghost" | "outline";
  size?: "icon" | "sm";
  indicator?: "toast" | "inline";
  successMessage?: string;
  errorMessage?: string;
  buttonText?: string;
  className?: string;
  showInitialText?: boolean;
};

type TextCopyButtonProps = BaseCopyButtonProps & {
  text: string;
  textCallback?: never;
};

type TextCallbackCopyButtonProps = BaseCopyButtonProps & {
  text?: never;
  textCallback: () => string;
};

type CopyButtonProps = TextCopyButtonProps | TextCallbackCopyButtonProps;

const CopyButton = ({
  variant = "ghost",
  size = "icon",
  indicator = "toast",
  successMessage = "Copied to clipboard",
  errorMessage = "Failed to copy",
  buttonText = "Copy",
  showInitialText = false,
  className,
  ...props
}: CopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [copiedOnce, setCopiedOnce] = useState(false);

  const handleCopy = async () => {
    try {
      let textToCopy: string;

      if ("text" in props) {
        textToCopy = props.text || "";
      } else if ("textCallback" in props) {
        textToCopy = props.textCallback();
      } else {
        throw new Error("Either text or textCallback must be provided");
      }

      await navigator.clipboard.writeText(textToCopy);

      if (indicator === "toast") {
        toast.success(successMessage);
      }

      setIsCopied(true);
      setCopiedOnce(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex items-center gap-1">
      {showInitialText && copiedOnce && (
        <span className="text-sm text-muted-foreground">Copied</span>
      )}
      <Button
        variant={variant}
        size={size}
        onClick={handleCopy}
        disabled={isCopied}
        className={className}
      >
        {isCopied ? (
          <CircleCheck className="w-4 h-4 text-green-600" />
        ) : (
          <CopyIcon className="w-4 h-4" />
        )}
        {size === "sm" && buttonText && (
          <span>{isCopied ? "Copied" : buttonText}</span>
        )}
      </Button>
    </div>
  );
};

export default CopyButton;
